'use client';

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import {
  Autocomplete,
  CircularProgress,
  TextField,
  type TextFieldProps,
} from '@/primitives';
import { SearchIcon } from '@/primitives/icons';
import { FilterOptionsState } from '@mui/material';
import { compactFilledTextFieldSx } from '@/lib/compactFilledFieldStyles';

export interface BaseSearchFieldApi {
  reset: () => void;
}

export interface BaseSearchFieldProps<TValue = string> {
  autoCompleteId?: string;
  disabled?: boolean;
  freeSolo?: boolean;
  InputLabelProps?: TextFieldProps['InputLabelProps'];
  InputProps?: TextFieldProps['InputProps'];
  fetchOptions: (inputValue: string) => Promise<readonly TValue[]>;
  fullWidth?: boolean;
  getOptionLabel: (option: string | TValue) => string;
  /** Smaller filled field typography (entity forms, breed lookup). */
  inputCompact?: boolean;
  isOptionEqualToValue: (option: TValue, value: TValue) => boolean;
  label: ReactNode;
  noOptionsText?: ReactNode;
  onChange?: (value: TValue | null) => void;
  placeholder: string;
  renderOption: (props: Object, option: TValue) => ReactNode;
  required?: boolean;
  value?: TValue | null;
}

const defaultNoOptionsText = 'No matches found';

function BaseSearchFieldInner<TValue>(
  {
    autoCompleteId,
    disabled,
    freeSolo,
    InputLabelProps,
    InputProps,
    fetchOptions,
    fullWidth,
    getOptionLabel,
    inputCompact = false,
    isOptionEqualToValue,
    label,
    noOptionsText,
    onChange,
    placeholder,
    renderOption,
    required,
    value,
  }: BaseSearchFieldProps<TValue>,
  ref: Ref<BaseSearchFieldApi>,
) {
  const [loading, setLoading] = useState(false);
  const [internalValue, setInternalValue] = useState<TValue | null>(
    value !== undefined ? value : null,
  );
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly TValue[]>([]);

  const fetchedOnce = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setInternalValue(value !== undefined ? value : null);
  }, [value]);

  useImperativeHandle(ref, () => {
    return {
      reset: () => {
        setInternalValue(null);
        setInputValue('');
        setOptions([]);
      },
    };
  }, []);

  useEffect(() => {
    if (inputValue) {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (internalValue && getOptionLabel(internalValue) === inputValue) {
        return;
      }

      setLoading(true);

      timerRef.current = setTimeout(() => {
        (async () => {
          const results = await fetchOptions(inputValue);
          const sorted = results.toSorted((a, b) =>
            getOptionLabel(a).localeCompare(getOptionLabel(b)),
          );
          setOptions(sorted);
          setLoading(false);
          fetchedOnce.current = true;
        })();
      }, 600);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        setLoading(false);
      }
    };
  }, [internalValue, inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // To turn of suggestion pop-up for Aucomplete
  useEffect(() => {
    if (autoCompleteId) {
      document
        .getElementById(autoCompleteId)
        ?.setAttribute('autocomplete', 'no-address-popup');
    }
    //eslint-disable-next-line
  }, []);

  // show defaultNoOptionsText when freeSolo = true
  function handleFilterOptions(
    options: TValue[],
    state: FilterOptionsState<TValue>,
  ) {
    if (
      freeSolo &&
      options.length === 0 &&
      fetchedOnce.current &&
      !loading &&
      state.inputValue
    ) {
      return [defaultNoOptionsText] as TValue[];
    }
    return options;
  }

  // show defaultNoOptionsText when freeSolo = true
  function handleRenderOption(props: Object, option: TValue) {
    return option === defaultNoOptionsText ? (
      <li
        {...props}
        key={defaultNoOptionsText}
        style={{ pointerEvents: 'none', color: 'gray' }}
      >
        {option as string}
      </li>
    ) : (
      renderOption(props, option)
    );
  }

  return (
    <Autocomplete
      id={autoCompleteId}
      disabled={disabled}
      freeSolo={freeSolo}
      getOptionLabel={getOptionLabel}
      filterOptions={handleFilterOptions}
      filterSelectedOptions
      fullWidth={fullWidth}
      includeInputInList
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={false}
      noOptionsText={noOptionsText || defaultNoOptionsText}
      onChange={(_event, newValue) => {
        if (!newValue) {
          setOptions([]);
          setInternalValue(null);
          onChange?.(null);
        } else if (typeof newValue === 'object') {
          setInternalValue(newValue);
          onChange?.(newValue);
        }
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          label={label}
          placeholder={placeholder}
          required={required}
          size={inputCompact ? 'small' : 'medium'}
          sx={inputCompact ? compactFilledTextFieldSx : undefined}
          InputLabelProps={{ ...InputLabelProps, ...params?.InputLabelProps }}
          InputProps={{
            ...params?.InputProps,
            ...InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    color="inherit"
                    size={inputCompact ? 16 : 20}
                    sx={{
                      marginTop: inputCompact ? '-12px' : '-16px',
                    }}
                  />
                ) : (
                  <SearchIcon
                    className="search-icon"
                    sx={{
                      position: 'absolute',
                      bottom: inputCompact ? '10px' : '14px',
                      right: inputCompact ? '6px' : '8px',
                      opacity: 0.54,
                      fontSize: inputCompact ? '1.125rem' : '1.25rem',
                      width: inputCompact ? '1.125rem' : '1.25rem',
                      height: inputCompact ? '1.125rem' : '1.25rem',
                    }}
                  />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              '&:has(input:not(:placeholder-shown)) .search-icon': {
                display: 'none',
              },
              '& input:placeholder-shown + MuiAutocomplete-endAdornment': {
                display: 'none',
              },
              ...InputProps?.sx,
            },
          }}
        />
      )}
      renderOption={handleRenderOption}
      value={internalValue}
    />
  );
}

// This is a workaround for forwardRef not passing through generic
// prop parameters
declare module 'react' {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: Ref<T>) => ReactNode | null,
  ): (props: P & RefAttributes<T>) => ReactNode | null;
}

export const BaseSearchField = forwardRef(BaseSearchFieldInner);
